/**
 * Preview-session policy for the auth layer, kept dependency-free.
 *
 * `isPreviewSession` is generic — it says nothing about who provisions the
 * preview. It's true when the app is served as a READ-ONLY preview of itself (a
 * disposable dev-server instance whose login is owned by a parent tab that keeps
 * the shared SSO cookie fresh). In that mode a dead token must never reload or
 * redirect to login (that only re-seeds the same expired cookie token and
 * thrashes) — the refresh path re-reads the shared cookie instead.
 *
 * Driven purely by env (set only in the preview's env file), like isDevelopment.
 */
export const isPreviewSession = process.env.NEXT_PUBLIC_PREVIEW_MODE === 'true';

/**
 * Seconds until a JWT's `exp` (negative if already past). Null when the token has
 * no readable numeric `exp` — used to tell whether a cookie token is still worth
 * adopting without carrying a separate `expires_in`.
 */
export function jwtExpiresInSeconds(token: string): number | null {
  try {
    const part = token.split('.')[1];
    if (!part) {
      return null;
    }
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: unknown };
    if (typeof payload.exp !== 'number') {
      return null;
    }
    return payload.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}
